/**
 * "User activity" signal for server-side sessions (Lot B).
 *
 * The server only extends the inactivity window of a session when the browser
 * says the user is really there. Background polling (notification bell 30 s,
 * chat 5 s) must NOT count, so the signal is not "a request was made" but
 * "the person touched the app recently":
 *   - pointer / key / touch input, or
 *   - the app (re)coming to the foreground, or being opened.
 * The flag is sent only on /auth/refresh and /auth/me as `X-Ridalo-Activity: 1`
 * (any non-GET request already counts on the server as a real action).
 * Nothing is stored: the timestamp lives in memory only.
 */
export const ACTIVITY_WINDOW_MS = 5 * 60 * 1000;
export const ACTIVITY_HEADER = 'X-Ridalo-Activity';

let lastActivity = Date.now();       // opening the app is activity

export const markUserActivity = (at = Date.now()) => { lastActivity = at; };
export const hasRecentActivity = (at = Date.now()) => at - lastActivity < ACTIVITY_WINDOW_MS;

let installed = false;
/** Idempotent: listens to real user input and to the app coming back to the foreground. */
export const installActivityTracking = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const touch = () => markUserActivity();
  ['pointerdown', 'keydown', 'touchstart'].forEach((e) => window.addEventListener(e, touch, { passive: true, capture: true }));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') markUserActivity(); });
  window.addEventListener('pageshow', touch);
};

/** Adds the flag to a request config when it targets /auth/refresh or /auth/me. */
export const withActivityHeader = (config) => {
  const url = (config?.url || '').toString();
  if ((url.includes('/auth/refresh') || url.includes('/auth/me')) && hasRecentActivity()) {
    config.headers = config.headers || {};
    config.headers[ACTIVITY_HEADER] = '1';
  }
  return config;
};
